import type { PortalSession } from './types'

/**
 * Persistencia de la sesión del portal, por pestaña.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * Por qué sessionStorage y no memoria (decisión revisada)
 * ─────────────────────────────────────────────────────────────────────────────
 * El primer diseño guardaba el token sólo en memoria, para que un XSS no se
 * llevara una sesión persistente. Es más seguro sobre el papel y en la práctica
 * rompía el producto: en el móvil, cualquier navegación completa —volver atrás,
 * una descarga que abre otra vista, iOS descartando la pestaña al cambiar de
 * app— perdía la sesión y le pedía la contraseña otra vez al cliente. Cada vez.
 *
 * El equilibrio: `sessionStorage`, no `localStorage`.
 *   - Muere al cerrar la pestaña, así que no queda nada en un móvil prestado.
 *   - No se comparte entre pestañas ni sobrevive al reinicio del navegador.
 *   - El token dura 2 h y sólo da acceso a UNA galería.
 *
 * Lo que se protege es la galería de la boda de quien ya tiene el enlace. Pedir
 * la contraseña en cada navegación es un fallo peor para el usuario principal
 * que el riesgo que evita.
 *
 * El token del ESTUDIO sigue siendo sólo-memoria: ahí sí hay privilegios reales.
 */

interface StoredSession extends PortalSession {
  /** Marca de tiempo absoluta: `expires_in` es relativo y no sobrevive a un reinicio. */
  expires_at: number
}

function key(linkToken: string): string {
  return `chasquitambo:session:${linkToken.slice(0, 16)}`
}

export function readSession(linkToken: string): PortalSession | null {
  try {
    const raw = sessionStorage.getItem(key(linkToken))
    if (raw === null) return null

    const stored = JSON.parse(raw) as StoredSession

    // Margen de 30 s: usar un token que caduca a mitad de petición daría un
    // 401 confuso justo después de haber "entrado".
    if (stored.expires_at - 30_000 < Date.now()) {
      sessionStorage.removeItem(key(linkToken))

      return null
    }

    return { token: stored.token, expires_in: stored.expires_in, visitor_id: stored.visitor_id }
  } catch {
    // Safari en navegación privada lanza al tocar el almacenamiento.
    return null
  }
}

export function writeSession(linkToken: string, session: PortalSession): void {
  try {
    const stored: StoredSession = {
      ...session,
      expires_at: Date.now() + session.expires_in * 1000,
    }

    sessionStorage.setItem(key(linkToken), JSON.stringify(stored))
  } catch {
    /* sin persistencia; la sesión actual sigue funcionando */
  }
}

export function clearSession(linkToken: string): void {
  try {
    sessionStorage.removeItem(key(linkToken))
  } catch {
    /* nada que limpiar */
  }
}
